import React, { useId, useState } from 'react';

import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { SUPPORTED_LANGUAGES, TranslatableString } from '../utils/translatable-string';

interface TranslatableInputProps {
  value: TranslatableString;
  onChange: (val: Record<string, string>) => void;
  label?: string;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  size?: 'small' | 'medium';
  placeholder?: string;
}

const LANG_LABELS: Record<string, string> = {
  en: 'EN',
  fr: 'FR',
  ar: 'AR',
  es: 'ES',
};

/**
 * TranslatableInput — Language-tabbed text input for multi-language fields.
 *
 * Shows EN | FR | AR | ES tabs above the field. A tab says whether that
 * translation is filled (ink) or still empty (muted), so a half-translated
 * field is visible without opening every language.
 * If incoming value is a plain string, initializes as { en: value }.
 */
export const TranslatableInput: React.FC<TranslatableInputProps> = ({
  value,
  onChange,
  label,
  required = false,
  multiline = false,
  rows = 1,
  placeholder,
}) => {
  const [activeLang, setActiveLang] = useState('en');
  const fieldId = useId();

  // Normalize value: plain string -> { en: value }
  const record: Record<string, string> =
    typeof value === 'string' ? { en: value } : (value ?? {});

  const handleChange = (text: string) => {
    onChange({ ...record, [activeLang]: text });
  };

  const fieldProps = {
    id: fieldId,
    value: record[activeLang] || '',
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange(e.target.value),
    placeholder: placeholder || `Enter ${LANG_LABELS[activeLang]} translation`,
    dir: activeLang === 'ar' ? ('rtl' as const) : ('ltr' as const),
  };

  return (
    <div>
      {label && (
        <Label htmlFor={fieldId} className="mb-1 block text-xs text-muted-foreground">
          {label}
          {required && <span className="text-destructive"> *</span>}
        </Label>
      )}

      <div className="mb-1 flex gap-0.5">
        {SUPPORTED_LANGUAGES.map((lang) => {
          const isFilled = !!record[lang]?.trim();
          const isActive = activeLang === lang;
          return (
            <button
              type="button"
              key={lang}
              onClick={() => setActiveLang(lang)}
              className={cn(
                'border-b-2 px-2.5 py-0.5 text-[11px] transition-colors select-none',
                isActive
                  ? 'border-foreground bg-muted font-medium text-foreground'
                  : 'border-transparent hover:text-foreground',
                !isActive && (isFilled ? 'text-foreground' : 'text-muted-foreground'),
              )}
            >
              {LANG_LABELS[lang]}
            </button>
          );
        })}
      </div>

      {multiline ? <Textarea rows={rows} {...fieldProps} /> : <Input {...fieldProps} />}
    </div>
  );
};

export default TranslatableInput;
